import { greys } from 'c1ui/theme/palette'
import type monaco from 'monaco-editor'

// Light theme colors based on design specs
const colors = {
    background: '#F5F7FB',
    foreground: greys[800], // #3E3A46
    string: '#378613', // success.main - green for strings
    keyword: '#5B39F5', // brand.main - purple for keywords
    delimiter: '#166534', // darker green for brackets/delimiters
    comment: greys[600], // #767184
    constant: '#3465C8', // primary.main
    variable: '#E4740C', // warning.main - orange for variables
    entity: '#5B39F5', // brand.main - purple for entities
    selection: '#CBDCFD', // primary.light
    lineHighlight: '#EBF3FE', // primary.extraLight
    cursor: greys[800],
    whitespace: greys[400],
    indentGuide: greys[300],
    indentGuideActive: greys[600],
    selectionBorder: greys[300],
    error: '#C22C1E', // error.main
    errorBackground: '#FFE4E4', // error.extraLight
    insertedBackground: '#E2F5D9', // success.extraLight
    insertedForeground: '#378613', // success.main
    deletedBackground: '#FFE4E4', // error.extraLight
    deletedForeground: '#C22C1E', // error.main
}

export const editorTheme: monaco.editor.IStandaloneThemeData = {
    base: 'vs',
    inherit: true,
    rules: [
        {
            background: 'F5F7FB',
            foreground: '3E3A46',
            token: '',
        },
        {
            foreground: '767184',
            token: 'comment',
        },
        {
            foreground: '767184',
            token: 'punctuation.definition.comment',
        },
        {
            foreground: '767184',
            token: 'string.comment',
        },
        {
            foreground: '3465C8',
            token: 'constant',
        },
        {
            foreground: '3465C8',
            token: 'entity.name.constant',
        },
        {
            foreground: '3465C8',
            token: 'variable.other.constant',
        },
        {
            foreground: '3465C8',
            token: 'variable.language',
        },
        {
            foreground: '5B39F5',
            token: 'entity',
        },
        {
            foreground: '5B39F5',
            token: 'entity.name',
        },
        {
            foreground: '3E3A46',
            token: 'variable.parameter.function',
        },
        {
            foreground: '378613',
            token: 'entity.name.tag',
        },
        {
            foreground: '5B39F5',
            token: 'keyword',
        },
        {
            foreground: '5B39F5',
            token: 'storage',
        },
        {
            foreground: '5B39F5',
            token: 'storage.type',
        },
        {
            foreground: '3E3A46',
            token: 'storage.modifier.package',
        },
        {
            foreground: '3E3A46',
            token: 'storage.modifier.import',
        },
        {
            foreground: '3E3A46',
            token: 'storage.type.java',
        },
        {
            foreground: '378613',
            token: 'string',
        },
        {
            foreground: '378613',
            token: 'punctuation.definition.string',
        },
        {
            foreground: '378613',
            token: 'string punctuation.section.embedded source',
        },
        {
            foreground: '3465C8',
            token: 'support',
        },
        {
            foreground: '3465C8',
            token: 'meta.property-name',
        },
        {
            foreground: 'E4740C',
            token: 'variable',
        },
        {
            foreground: '3E3A46',
            token: 'variable.other',
        },
        {
            foreground: 'C22C1E',
            fontStyle: 'bold italic underline',
            token: 'invalid.broken',
        },
        {
            foreground: 'C22C1E',
            fontStyle: 'bold italic underline',
            token: 'invalid.deprecated',
        },
        {
            foreground: 'FFFFFF',
            background: 'C22C1E',
            fontStyle: 'italic underline',
            token: 'invalid.illegal',
        },
        {
            foreground: 'FFFFFF',
            background: 'C22C1E',
            fontStyle: 'italic underline',
            token: 'carriage-return',
        },
        {
            foreground: 'C22C1E',
            fontStyle: 'bold italic underline',
            token: 'invalid.unimplemented',
        },
        {
            foreground: 'C22C1E',
            token: 'message.error',
        },
        {
            foreground: '3E3A46',
            token: 'string source',
        },
        {
            foreground: '3465C8',
            token: 'string variable',
        },
        {
            foreground: '378613',
            token: 'source.regexp',
        },
        {
            foreground: '378613',
            token: 'string.regexp',
        },
        {
            foreground: '378613',
            token: 'string.regexp.character-class',
        },
        {
            foreground: '378613',
            token: 'string.regexp constant.character.escape',
        },
        {
            foreground: '378613',
            token: 'string.regexp source.ruby.embedded',
        },
        {
            foreground: '378613',
            token: 'string.regexp string.regexp.arbitrary-repitition',
        },
        {
            foreground: '378613',
            fontStyle: 'bold',
            token: 'string.regexp constant.character.escape',
        },
        {
            foreground: '3465C8',
            token: 'support.constant',
        },
        {
            foreground: '3465C8',
            token: 'support.variable',
        },
        {
            foreground: '3465C8',
            token: 'meta.module-reference',
        },
        {
            foreground: 'E4740C',
            token: 'markup.list',
        },
        {
            foreground: '3465C8',
            fontStyle: 'bold',
            token: 'markup.heading',
        },
        {
            foreground: '3465C8',
            fontStyle: 'bold',
            token: 'markup.heading entity.name',
        },
        {
            foreground: '5B5766',
            token: 'markup.quote',
        },
        {
            foreground: '3E3A46',
            fontStyle: 'italic',
            token: 'markup.italic',
        },
        {
            foreground: '3E3A46',
            fontStyle: 'bold',
            token: 'markup.bold',
        },
        {
            foreground: '3465C8',
            token: 'markup.raw',
        },
        {
            foreground: 'C22C1E',
            background: 'FFE4E4',
            token: 'markup.deleted',
        },
        {
            foreground: 'C22C1E',
            background: 'FFE4E4',
            token: 'meta.diff.header.from-file',
        },
        {
            foreground: 'C22C1E',
            background: 'FFE4E4',
            token: 'punctuation.definition.deleted',
        },
        {
            foreground: '378613',
            background: 'E2F5D9',
            token: 'markup.inserted',
        },
        {
            foreground: '378613',
            background: 'E2F5D9',
            token: 'meta.diff.header.to-file',
        },
        {
            foreground: '378613',
            background: 'E2F5D9',
            token: 'punctuation.definition.inserted',
        },
        {
            foreground: 'E4740C',
            background: 'FFE9D4',
            token: 'markup.changed',
        },
        {
            foreground: 'E4740C',
            background: 'FFE9D4',
            token: 'punctuation.definition.changed',
        },
        {
            foreground: '767184',
            background: 'F0F0F1',
            token: 'markup.ignored',
        },
        {
            foreground: '767184',
            background: 'F0F0F1',
            token: 'markup.untracked',
        },
        {
            foreground: '5B39F5',
            fontStyle: 'bold',
            token: 'meta.diff.range',
        },
        {
            foreground: '3465C8',
            token: 'meta.diff.header',
        },
        {
            foreground: '3465C8',
            fontStyle: 'bold',
            token: 'meta.separator',
        },
        {
            foreground: '3465C8',
            token: 'meta.output',
        },
        {
            foreground: '166534',
            token: 'brackethighlighter.tag',
        },
        {
            foreground: '166534',
            token: 'brackethighlighter.curly',
        },
        {
            foreground: '166534',
            token: 'brackethighlighter.round',
        },
        {
            foreground: '166534',
            token: 'brackethighlighter.square',
        },
        {
            foreground: '166534',
            token: 'brackethighlighter.angle',
        },
        {
            foreground: '166534',
            token: 'brackethighlighter.quote',
        },
        {
            foreground: 'C22C1E',
            token: 'brackethighlighter.unmatched',
        },
        {
            foreground: 'C22C1E',
            token: 'sublimelinter.mark.error',
        },
        {
            foreground: 'E4740C',
            token: 'sublimelinter.mark.warning',
        },
        {
            foreground: '908C9B',
            token: 'sublimelinter.gutter-mark',
        },
        {
            foreground: '3465C8',
            fontStyle: 'underline',
            token: 'constant.other.reference.link',
        },
        {
            foreground: '3465C8',
            fontStyle: 'underline',
            token: 'string.other.link',
        },
        // CEL-specific tokens for delimiters
        {
            foreground: '166534',
            token: 'delimiter',
        },
        {
            foreground: '166534',
            token: 'delimiter.curly',
        },
        {
            foreground: '166534',
            token: 'delimiter.parenthesis',
        },
        {
            foreground: '166534',
            token: 'delimiter.square',
        },
        // Identifier styling
        {
            foreground: '3E3A46',
            token: 'identifier',
        },
    ],
    colors: {
        'editor.foreground': colors.foreground,
        'editor.background': colors.background,
        'editor.selectionBackground': colors.selection,
        'editor.inactiveSelectionBackground': '#E6E6E8',
        'editor.lineHighlightBackground': colors.lineHighlight,
        'editorCursor.foreground': colors.cursor,
        'editorWhitespace.foreground': colors.whitespace,
        'editorIndentGuide.background': colors.indentGuide,
        'editorIndentGuide.activeBackground': colors.indentGuideActive,
        'editor.selectionHighlightBorder': colors.selectionBorder,
        'editorLineNumber.foreground': greys[500],
        'editorLineNumber.activeForeground': greys[700],
        'editorBracketMatch.background': '#CBDCFD50',
        'editorBracketMatch.border': '#3465C8',
        'editorError.foreground': colors.error,
        'editorWarning.foreground': '#E4740C',
        'editorInfo.foreground': '#3465C8',
        'editorHint.foreground': greys[600],
        'editorGutter.background': colors.background,
        'editorWidget.background': '#FFFFFF',
        'editorWidget.border': greys[200],
        'editorSuggestWidget.background': '#FFFFFF',
        'editorSuggestWidget.border': greys[200],
        'editorSuggestWidget.foreground': '#1A1A1A',
        'editorSuggestWidget.selectedForeground': '#1A1A1A',
        'editorSuggestWidget.focusHighlightForeground': '#5B39F5',
        'editorSuggestWidget.selectedBackground': '#EBF3FE',
        'editorSuggestWidget.highlightForeground': '#5B39F5',
        'editorHoverWidget.background': '#FFFFFF',
        'editorHoverWidget.border': greys[200],
        'scrollbar.shadow': '#00000010',
        'scrollbarSlider.background': `${greys[300]}80`,
        'scrollbarSlider.hoverBackground': `${greys[400]}80`,
        'scrollbarSlider.activeBackground': `${greys[500]}80`,
    },
}

// Export background color for use in wrapper component
export const editorBackgroundColor = colors.background
